<template>
  <div class="hms-page">
    <Navigation />

    <div class="container hms-page-body">
      <div class="d-flex align-items-center justify-content-between mb-4 hms-page-header">
        <div class="d-flex align-items-center gap-3">
          <a href="javascript:void(0)" @click="$router.push('/dashboard')" class="hms-back" title="Back">
            <i class="mdi mdi-arrow-left"></i>
          </a>
          <h4 class="hms-page-title m-0"><i class="mdi mdi-account-circle me-2"></i>{{ $t('page.profile', 'My Profile') }}</h4>
        </div>
        <div class="d-flex gap-2">
          <button class="hms-btn hms-btn-outline" @click="$router.push('/security')">
            <i class="mdi mdi-shield-lock me-1"></i>Security &amp; MFA
          </button>
          <button class="hms-btn hms-btn-blue" @click="$router.push('/change-password')">
            <i class="mdi mdi-key-variant me-1"></i>Change Password
          </button>
        </div>
      </div>

      <div v-if="loading" class="hms-spinner"><div class="spinner-border"></div></div>

      <div v-else class="row g-3">
        <!-- LEFT — IDENTITY CARD -->
        <div class="col-lg-4">
          <div class="hms-card hms-identity">
            <div class="hms-avatar-xl">
              <img v-if="photoUrl" :src="photoUrl" alt="" class="hms-avatar-img" />
              <span v-else>{{ initials }}</span>
            </div>
            <div class="hms-id-name">{{ fullName || profile.username }}</div>
            <div class="hms-muted-sm hms-mono">@{{ profile.username }}</div>
            <span class="hms-badge hms-badge-info mt-2">{{ profile.role_name || '—' }}</span>

            <div class="hms-id-section">
              <div class="hms-id-row"><i class="mdi mdi-hospital-building"></i> <span>{{ profile.hospital_name || '—' }}</span></div>
              <div class="hms-id-row"><i class="mdi mdi-source-branch"></i> <span>{{ profile.branch_name || '—' }}</span></div>
              <div class="hms-id-row" v-if="profile.staff_first">
                <i class="mdi mdi-account-tie"></i>
                <span>{{ profile.staff_first }} {{ profile.staff_last }} <span class="hms-muted-sm">· {{ profile.staff_position }}</span></span>
              </div>
            </div>

            <div class="hms-id-section">
              <div class="hms-id-row"><i class="mdi mdi-clock-outline"></i> <span>Last login: {{ profile.last_login ? formatDateTime(profile.last_login) : 'Never' }}</span></div>
              <div class="hms-id-row"><i class="mdi mdi-calendar-plus"></i> <span>Joined: {{ formatDateTime(profile.created_at) }}</span></div>
            </div>

            <div v-if="profile.must_change_password == 1" class="hms-alert hms-alert-warning mt-3">
              <i class="mdi mdi-key-alert me-1"></i>You must change your password.
            </div>
          </div>
        </div>

        <!-- RIGHT — EDITABLE FIELDS + ACTIVITY -->
        <div class="col-lg-8">
          <div class="hms-card">
            <div class="hms-section-label">EDIT PROFILE</div>
            <div class="row g-3 mb-3">
              <div class="col-md-6"><label class="hms-label">First Name</label>
                <input class="hms-input" v-model="form.first_name" />
              </div>
              <div class="col-md-6"><label class="hms-label">Last Name</label>
                <input class="hms-input" v-model="form.last_name" />
              </div>
              <div class="col-md-6"><label class="hms-label">Email</label>
                <input class="hms-input" type="email" v-model="form.email" />
              </div>
              <div class="col-md-6"><label class="hms-label">Phone</label>
                <input class="hms-input" v-model="form.phone" />
              </div>
              <div class="col-12">
                <label class="hms-label">Profile Photo</label>
                <div class="d-flex align-items-center gap-3">
                  <img v-if="photoUrl" :src="photoUrl" class="hms-photo-preview" alt="" />
                  <div v-else class="hms-photo-ph"><i class="mdi mdi-account"></i></div>
                  <div>
                    <input ref="photoInput" type="file" accept="image/png,image/jpeg,image/gif,image/webp" style="display:none" @change="onPhotoPicked" />
                    <button class="hms-btn hms-btn-blue" :disabled="uploadingPhoto" @click="$refs.photoInput.click()">
                      <span v-if="uploadingPhoto" class="spinner-border spinner-border-sm me-1"></span>
                      <i v-else class="mdi mdi-camera me-1"></i>{{ uploadingPhoto ? 'Uploading…' : 'Upload photo' }}
                    </button>
                    <div class="hms-muted-sm mt-1">PNG / JPG / GIF / WEBP, max 5 MB.</div>
                  </div>
                </div>
              </div>
            </div>
            <button class="hms-btn hms-btn-primary" @click="save" :disabled="processing">
              <span v-if="processing" class="spinner-border spinner-border-sm me-1"></span>
              {{ processing ? 'Saving...' : 'Save Changes' }}
            </button>
          </div>

          <!-- Shift workload (FB#1-36/38) — patients attended per shift -->
          <div class="hms-card">
            <div class="d-flex justify-content-between align-items-center mb-2">
              <div class="hms-section-label m-0">SHIFT WORKLOAD — PATIENTS ATTENDED PER SHIFT</div>
              <div class="d-flex gap-2 align-items-center">
                <input type="date" class="hms-input hms-filter" v-model="wlFrom" @change="loadWorkload" />
                <input type="date" class="hms-input hms-filter" v-model="wlTo"   @change="loadWorkload" />
              </div>
            </div>
            <div v-if="workload && workload.totals" class="hms-stat-row mb-2">
              <div class="hms-stat"><div class="hms-stat-num">{{ workload.totals.shifts }}</div><div class="hms-stat-lbl">Shifts</div></div>
              <div class="hms-stat"><div class="hms-stat-num">{{ workload.totals.patients_attended }}</div><div class="hms-stat-lbl">Distinct patients</div></div>
              <div class="hms-stat"><div class="hms-stat-num">{{ workload.totals.visits }}</div><div class="hms-stat-lbl">Visits in shifts</div></div>
            </div>
            <div v-if="workloadLoading" class="hms-muted-sm"><span class="spinner-border spinner-border-sm me-1"></span>Loading…</div>
            <div v-else-if="workload && workload.shifts && workload.shifts.length" style="overflow-x:auto;">
              <table class="hms-table">
                <thead><tr><th>Date</th><th>Shift</th><th>From – To</th><th>Status</th><th class="text-end">Patients</th><th class="text-end">Visits</th></tr></thead>
                <tbody>
                  <tr v-for="s in workload.shifts" :key="s.shift_id">
                    <td>{{ formatDate(s.shift_date) }}</td>
                    <td>{{ cap(s.shift_type) }}</td>
                    <td class="hms-muted-sm">{{ (s.start_time||'').slice(0,5) }} – {{ (s.end_time||'').slice(0,5) || '—' }}</td>
                    <td><span class="hms-badge" :class="shiftStatusBadge(s.status)">{{ cap(s.status) }}</span></td>
                    <td class="text-end fw-bold">{{ s.patients_attended }}</td>
                    <td class="text-end">{{ s.visits_count }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div v-else class="hms-muted">No shifts in this date range, or no staff record linked to this user.</div>
          </div>

          <div class="hms-card">
            <div class="hms-section-label">RECENT ACTIVITY</div>
            <div v-if="activity.length" class="hms-activity-list">
              <div v-for="a in activity" :key="a.log_id" class="hms-activity-row">
                <div class="hms-activity-dot"></div>
                <div class="flex-fill">
                  <div class="hms-text">{{ a.description || a.action }}</div>
                  <div class="hms-muted-sm">{{ a.module || '—' }} · {{ formatDateTime(a.created_at) }}{{ a.ip_address ? ' · ' + a.ip_address : '' }}</div>
                </div>
              </div>
            </div>
            <div v-else class="hms-muted">No recent activity.</div>
          </div>
        </div>
      </div>

      <!-- Toast -->
      <transition name="hms-toast">
        <div v-if="toastMsg" class="hms-toast" :class="{ 'hms-toast-error': toastError }">
          <i class="mdi me-2" :class="toastError ? 'mdi-alert-circle' : 'mdi-check-circle'"></i>{{ toastMsg }}
        </div>
      </transition>
    </div>
  </div>
</template>

<style>
:root, html[data-theme="dark"] {
  --hms-h:#f0f0f0;--hms-s:#a0a0a0;--hms-m:#808080;
  --hms-bg1:#141414;--hms-bg2:#0a0a0a;--hms-bg3:#1e1e1e;--hms-bg4:#0f0f0f;
  --hms-br:#2a2a2a;--hms-input-br:#333333;
  --hms-primary:#1a7fe8;--hms-primary-lt:rgba(26,127,232,0.12);
  --hms-success:#28a745;--hms-danger:#dc3545;--hms-warning:#ffc107;
}
html[data-theme="light"] {
  --hms-h:#1a1a1a;--hms-s:#555555;--hms-m:#777777;
  --hms-bg1:#ffffff;--hms-bg2:#f4f6f9;--hms-bg3:#eef1f5;--hms-bg4:#ffffff;
  --hms-br:#dee2e8;--hms-input-br:#ced4da;
  --hms-primary:#0047AB;--hms-primary-lt:rgba(0,71,171,0.08);
  --hms-success:#28a745;--hms-danger:#dc3545;--hms-warning:#ffc107;
}
html[data-theme="light"] body, html[data-theme="dark"] body { background: var(--hms-bg2) !important; color: var(--hms-h) !important; }
</style>

<style scoped>
.hms-page { min-height: 100vh; background: var(--hms-bg2); padding-top: 70px; color: var(--hms-h); }
.hms-page-body { padding-top: 20px; padding-bottom: 40px; }
.hms-page-header { flex-wrap: wrap; gap: 12px; }
.hms-back { color: var(--hms-s); font-size: 22px; padding: 4px; }
.hms-back:hover { color: var(--hms-primary); }
.hms-page-title { color: var(--hms-h); font-size: 20px; font-weight: 700; }

.hms-muted { color: var(--hms-s); font-size: 13px; }
.hms-muted-sm { color: var(--hms-s); font-size: 12px; }
.hms-text { color: var(--hms-h); }
.hms-mono { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 12px; }

.hms-card { background: var(--hms-bg1); border: 1px solid var(--hms-br); border-radius: 14px; padding: 22px; margin-bottom: 16px; }
.hms-spinner { display: flex; justify-content: center; padding: 40px; }
.hms-spinner .spinner-border { color: var(--hms-primary); }

.hms-input { width: 100%; background: var(--hms-bg4); border: 1px solid var(--hms-input-br); border-radius: 8px; padding: 8px 14px; color: var(--hms-h); font-size: 14px; outline: none; }
.hms-input:focus { border-color: var(--hms-primary); }
.hms-label { color: var(--hms-m); font-size: 12px; font-weight: 600; margin-bottom: 4px; display: block; }
.hms-section-label { font-size: 11px; font-weight: 700; color: var(--hms-s); letter-spacing: 1.5px; margin-bottom: 12px; }

.hms-identity { text-align: center; }
.hms-avatar-xl { width: 96px; height: 96px; border-radius: 50%; background: var(--hms-primary-lt); color: var(--hms-primary); display: flex; align-items: center; justify-content: center; font-size: 36px; font-weight: 800; margin: 0 auto 12px; overflow: hidden; }
.hms-avatar-img { width: 100%; height: 100%; object-fit: cover; }
.hms-photo-preview { width: 64px; height: 64px; border-radius: 50%; object-fit: cover; border: 1px solid var(--hms-br); }
.hms-photo-ph { width: 64px; height: 64px; border-radius: 50%; background: var(--hms-bg3); color: var(--hms-s); display: flex; align-items: center; justify-content: center; font-size: 28px; }
.hms-id-name { color: var(--hms-h); font-size: 18px; font-weight: 700; }
.hms-id-section { margin-top: 16px; padding-top: 16px; border-top: 1px solid var(--hms-br); text-align: left; }
.hms-id-row { display: flex; gap: 8px; align-items: center; padding: 4px 0; font-size: 13px; color: var(--hms-h); }
.hms-id-row i { color: var(--hms-primary); width: 18px; }

.hms-badge { padding: 4px 12px; border-radius: 50px; font-size: 11px; font-weight: 600; display: inline-block; }
.hms-badge-info { background: var(--hms-primary-lt); color: var(--hms-primary); }

.hms-btn { border: none; border-radius: 8px; padding: 8px 16px; font-size: 13px; font-weight: 600; cursor: pointer; transition: all .15s; display: inline-flex; align-items: center; justify-content: center; }
.hms-btn:disabled { opacity: 0.6; cursor: not-allowed; }
.hms-btn-primary { background: var(--hms-primary); color: #fff; }
.hms-btn-primary:hover:not(:disabled) { filter: brightness(1.1); }
.hms-btn-blue { background: var(--hms-primary-lt); color: var(--hms-primary); border: 1px solid var(--hms-primary-lt); }
.hms-btn-blue:hover:not(:disabled) { background: var(--hms-primary); color: #fff; }

.hms-alert { padding: 10px 14px; border-radius: 8px; font-size: 13px; }
.hms-alert-warning { background: rgba(255,193,7,.15); color: var(--hms-warning); border: 1px solid rgba(255,193,7,.3); }

.hms-stat-row { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; }
.hms-stat { background: var(--hms-bg2); border: 1px solid var(--hms-br); border-radius: 10px; padding: 10px 12px; }
.hms-stat-num { font-size: 20px; font-weight: 800; color: var(--hms-h); line-height: 1.1; }
.hms-stat-lbl { font-size: 11px; color: var(--hms-s); text-transform: uppercase; letter-spacing: .5px; margin-top: 2px; }
.hms-table { width: 100%; border-collapse: collapse; }
.hms-table th { color: var(--hms-s); font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 1px; padding: 8px 10px; border-bottom: 1px solid var(--hms-br); text-align: left; }
.hms-table td { color: var(--hms-h); font-size: 13px; padding: 8px 10px; border-bottom: 1px solid var(--hms-br); }
.text-end { text-align: right; }
.hms-badge { padding: 2px 8px; border-radius: 50px; font-size: 11px; font-weight: 600; display: inline-block; }
.hms-badge-success { background: rgba(40,167,69,.15); color: var(--hms-success); }
.hms-badge-info { background: rgba(26,127,232,.15); color: var(--hms-primary); }
.hms-badge-warning { background: rgba(255,193,7,.15); color: #b8860b; }
.hms-badge-neutral { background: var(--hms-bg3); color: var(--hms-s); }
.hms-filter { width: auto; padding: 5px 10px; font-size: 12px; }
.hms-activity-list { display: flex; flex-direction: column; gap: 6px; max-height: 480px; overflow-y: auto; }
.hms-activity-row { display: flex; gap: 10px; padding: 8px 0; border-bottom: 1px dashed var(--hms-br); }
.hms-activity-row:last-child { border-bottom: none; }
.hms-activity-dot { width: 8px; height: 8px; border-radius: 50%; background: var(--hms-primary); margin-top: 6px; flex-shrink: 0; }

.hms-toast { position: fixed; bottom: 24px; right: 24px; background: var(--hms-bg1); border: 1px solid var(--hms-success); color: var(--hms-h); padding: 12px 18px; border-radius: 10px; box-shadow: 0 12px 32px rgba(40,167,69,.2); z-index: 1000; font-size: 13px; display: flex; align-items: center; }
.hms-toast i { color: var(--hms-success); }
.hms-toast.hms-toast-error { border-color: var(--hms-danger); box-shadow: 0 12px 32px rgba(220,53,69,.2); }
.hms-toast.hms-toast-error i { color: var(--hms-danger); }
.hms-toast-enter-active, .hms-toast-leave-active { transition: all .25s ease; }
.hms-toast-enter-from, .hms-toast-leave-to { opacity: 0; transform: translateY(20px); }
</style>

<script>
/* eslint-disable */
import Navigation from "@/components/hms-auth/Navigation.vue";
import { executeJson, hms, base } from "@/api";

export default {
  name: "Profile",
  components: { Navigation },
  data() {
    return {
      loading: true, processing: false, uploadingPhoto: false,
      profile: {}, activity: [],
      form: { first_name: "", last_name: "", email: "", phone: "", profile_photo: "" },
      workload: null, workloadLoading: false,
      wlFrom: (function(){ var d = new Date(); d.setDate(d.getDate() - 30); return d.toISOString().slice(0,10); })(),
      wlTo:   (function(){ return new Date().toISOString().slice(0,10); })(),
      toastMsg: "", toastError: false, toastTimer: null
    };
  },
  computed: {
    photoUrl() {
      var p = this.form.profile_photo || this.profile.profile_photo;
      if (!p) return "";
      if (/^https?:\/\//i.test(p)) return p;
      return (base || "").replace(/\/$/, "") + "/" + String(p).replace(/^\//, "");
    },
    fullName() { return ((this.profile.first_name||"") + " " + (this.profile.last_name||"")).trim(); },
    initials() {
      var f = (this.profile.first_name||"").trim().charAt(0);
      var l = (this.profile.last_name||"").trim().charAt(0);
      return ((f + l).toUpperCase()) || (this.profile.username || "?").charAt(0).toUpperCase();
    }
  },
  mounted() {
    if (!sessionStorage.getItem("isLoggedIn")) { this.$router.push("/login"); return; }
    this.applyTheme();
    this.load();
  },
  beforeUnmount() { if (this.toastTimer) clearTimeout(this.toastTimer); },
  methods: {
    applyTheme() {
      var t = localStorage.getItem("hms_theme") || document.documentElement.getAttribute("data-theme") || "light";
      document.documentElement.setAttribute("data-theme", t);
    },
    showToast(msg, isError) {
      this.toastMsg = msg; this.toastError = !!isError;
      if (this.toastTimer) clearTimeout(this.toastTimer);
      var self = this;
      this.toastTimer = setTimeout(() => { self.toastMsg = ""; }, 2400);
    },
    formatDateTime(s) { if (!s) return "—"; var d = new Date(s.replace ? s.replace(" ","T") : s); return isNaN(d) ? s : d.toLocaleString(); },

    async load() {
      this.loading = true;
      try {
        var r = await executeJson({ function: "getMyProfile" }, hms);
        if (r.data && r.data.success) {
          this.profile = (r.data.data && r.data.data.Profile) || {};
          this.activity= (r.data.data && r.data.data.Activity) || [];
          this.form = {
            first_name: this.profile.first_name || "",
            last_name:  this.profile.last_name  || "",
            email:      this.profile.email || "",
            phone:      this.profile.phone || "",
            profile_photo: this.profile.profile_photo || ""
          };
        }
      } catch(e) { console.error(e); }
      this.loading = false;
      this.loadWorkload();
    },

    async loadWorkload() {
      this.workloadLoading = true;
      try {
        var r = await executeJson({ function: "getStaffShiftWorkload", date_from: this.wlFrom, date_to: this.wlTo }, hms);
        if (r.data && r.data.success) this.workload = r.data.data || null;
      } catch (e) { /* non-fatal */ }
      this.workloadLoading = false;
    },
    cap(s) { return s ? (s.charAt(0).toUpperCase() + s.slice(1).replace(/_/g,' ')) : ''; },
    shiftStatusBadge(s) { return { scheduled:'hms-badge-info', active:'hms-badge-success', completed:'hms-badge-neutral', absent:'hms-badge-warning', swapped:'hms-badge-neutral' }[s] || 'hms-badge-neutral'; },

    onPhotoPicked(e) {
      var file = e.target.files && e.target.files[0];
      if (!file) return;
      if (file.size > 5 * 1024 * 1024) { this.showToast("Image must be ≤ 5 MB", true); return; }
      var self = this;
      var reader = new FileReader();
      reader.onload = function() { self.doUploadPhoto(reader.result, file.name); };
      reader.readAsDataURL(file);
    },
    async doUploadPhoto(dataUrl, name) {
      this.uploadingPhoto = true;
      try {
        var r = await executeJson({ function: "uploadProfilePhoto", file_base64: dataUrl, file_name: name }, hms);
        if (r.data && r.data.success) {
          var wp = r.data.data.profile_photo;
          this.form.profile_photo = wp;
          this.profile.profile_photo = wp;
          try { sessionStorage.setItem("userPhoto", wp); } catch (e) {}
          this.showToast("Photo updated");
        } else { this.showToast((r.data && r.data.message) || "Upload failed", true); }
      } catch (e) { this.showToast("Connection error", true); }
      this.uploadingPhoto = false;
      if (this.$refs.photoInput) this.$refs.photoInput.value = "";
    },
    async save() {
      this.processing = true;
      try {
        var r = await executeJson(Object.assign({ function: "updateMyProfile" }, this.form), hms);
        if (r.data && r.data.success) { this.showToast("Profile saved"); await this.load(); }
        else { this.showToast(r.data.message || "Failed", true); }
      } catch(e) { this.showToast("Connection error", true); }
      this.processing = false;
    }
  }
};
</script>
